/**
 * Relationship Entry Types
 *
 * TypeScript interfaces for relationship tracker daily sentiment entries.
 * Includes event flags (boolean) and optional feeling scales (nullable 1-10).
 */

export interface RelationshipEntry {
  id: string;
  user_id: string;
  entry_date: string;
  rating: number;
  notes: string | null;

  // Event flags
  had_meaningful_conversation: boolean;
  had_conflict: boolean;
  had_physical_affection: boolean;
  had_intimacy: boolean;
  had_quality_time: boolean;
  had_support: boolean;
  had_repair_attempt: boolean;
  spent_apart: boolean;

  // Optional feeling scales
  my_mood: number | null;
  partner_mood_guess: number | null;
  connection_felt: number | null;

  created_at: string;
  updated_at: string;
}

export interface RelationshipEntryCreate {
  entry_date: string;
  rating: number;
  notes?: string | null;

  had_meaningful_conversation?: boolean;
  had_conflict?: boolean;
  had_physical_affection?: boolean;
  had_intimacy?: boolean;
  had_quality_time?: boolean;
  had_support?: boolean;
  had_repair_attempt?: boolean;
  spent_apart?: boolean;

  my_mood?: number | null;
  partner_mood_guess?: number | null;
  connection_felt?: number | null;
}

export interface RelationshipEntryUpdate {
  rating?: number;
  notes?: string | null;

  had_meaningful_conversation?: boolean;
  had_conflict?: boolean;
  had_physical_affection?: boolean;
  had_intimacy?: boolean;
  had_quality_time?: boolean;
  had_support?: boolean;
  had_repair_attempt?: boolean;
  spent_apart?: boolean;

  my_mood?: number | null;
  partner_mood_guess?: number | null;
  connection_felt?: number | null;
}

export interface RelationshipEntryListResponse {
  entries: RelationshipEntry[];
  total_count: number;
}

export interface RelationshipEntryStats {
  count: number;
  average_rating: number | null;
  current_streak: number;
}

/** Event flag field keys in display order */
export const EVENT_FIELDS = [
  "had_meaningful_conversation",
  "had_conflict",
  "had_physical_affection",
  "had_intimacy",
  "had_quality_time",
  "had_support",
  "had_repair_attempt",
  "spent_apart",
] as const;

export type EventField = (typeof EVENT_FIELDS)[number];

/** Display config for event flags — label, icon name, short label for calendar */
export const EVENT_CONFIG: Record<
  EventField,
  { label: string; shortLabel: string; description: string }
> = {
  had_meaningful_conversation: {
    label: "Meaningful conversation",
    shortLabel: "Talked",
    description: "A real conversation beyond logistics",
  },
  had_conflict: {
    label: "Conflict / argument",
    shortLabel: "Conflict",
    description: "A disagreement or argument",
  },
  had_physical_affection: {
    label: "Physical affection",
    shortLabel: "Affection",
    description: "Hugs, cuddling, non-sexual touch",
  },
  had_intimacy: {
    label: "Intimacy",
    shortLabel: "Intimacy",
    description: "Sexual connection",
  },
  had_quality_time: {
    label: "Quality time",
    shortLabel: "Time",
    description: "Shared activity, meal, or focused time",
  },
  had_support: {
    label: "Acts of support",
    shortLabel: "Support",
    description: "One partner helped or supported the other",
  },
  had_repair_attempt: {
    label: "Repair attempt",
    shortLabel: "Repair",
    description: "Effort to reconnect after tension",
  },
  spent_apart: {
    label: "Spent apart",
    shortLabel: "Apart",
    description: "One of you was away or distant",
  },
};
